import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Logo, Icon } from '../brand/Primitives';
import { useAuthModal } from './AuthModalContext';
import { supabase } from '../../supabaseClient';
import { getOrCreateDatabaseUser } from '../../services/userMappingService';
import { UserContext } from '../../UserContext';
import { useI18n } from '../i18n';

export default function AuthModal() {
  const { open, mode, closeAuth, setMode } = useAuthModal();
  const navigate = useNavigate();
  const { setUserId } = useContext(UserContext);
  const { t } = useI18n();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Gate signup on an explicit agreement to Terms + Privacy. Required for
  // our liability posture on an allergy-safety product — a new user has
  // to actively opt in, not just click "Create account." Signin doesn't
  // need this (they agreed when they signed up).
  const [agreed, setAgreed] = useState(false);

  const isSignup = mode === 'signup';

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && closeAuth();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeAuth]);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setPassword('');
      setName('');
      setShowPw(false);
      setError(null);
      setSubmitting(false);
      setAgreed(false);
    }
  }, [open]);

  if (!open) return null;

  const handleSuccess = (userId) => {
    localStorage.setItem('userId', String(userId));
    setUserId(userId);
    closeAuth();
    // Unified context: new signups route to onboarding (unless they already
    // finished it in a previous session); returning users go to dashboard.
    //
    // `/` is the UnifiedLanding route — users who opened the modal from the
    // new landing expect to land on the unified dashboard, not the legacy
    // MUI one. Only *real* legacy entry points (e.g. /login, /create-account
    // — which don't actually use this modal, but belt-and-suspenders) fall
    // through to the legacy /dashboard. Previously this sent unified-landing
    // sign-ins to the legacy dashboard, which looked wrong.
    const path = window.location.pathname;
    const inUnified = path === '/' || path.startsWith('/unified');
    if (!inUnified) { navigate('/dashboard'); return; }
    const onboardingDone = localStorage.getItem('cwm_onboarding_done') === '1';
    if (isSignup && !onboardingDone) {
      navigate('/unified/onboarding');
    } else {
      navigate('/unified/dashboard');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (isSignup && !agreed) {
      setError(t('auth.err_must_agree'));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // Real Supabase auth — same flow as the legacy LoginScreen /
      // CreateAccountScreen, just inside the unified modal. Previously
      // this modal called the direct /api/loginUser endpoint which
      // left users without a Supabase session → "ghost logged in"
      // state that evaporated on refresh.
      let authError = null;
      let session = null;

      if (isSignup) {
        const { data, error } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            data: {
              name,
              termsAccepted: true,
              termsAcceptedAt: new Date().toISOString(),
            },
          },
        });
        authError = error;
        session = data?.session;
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        authError = error;
        session = data?.session;
      }

      if (authError) {
        setError(authError.message || t('auth.err_invalid'));
        return;
      }

      // Signup with email confirmation required returns a user but no
      // session — surface that as a friendly message rather than a
      // cryptic failure.
      if (isSignup && !session) {
        setError('Account created! Check your email to confirm your account, then sign in.');
        return;
      }

      // Resolve Supabase UUID → legacy integer user id via the
      // server-side mapping service. Matches how LoginScreen +
      // CreateAccountScreen do it on the prod surface.
      const legacyUserId = await getOrCreateDatabaseUser();
      if (!legacyUserId) {
        setError('Signed in, but could not load your profile. Try again.');
        return;
      }
      handleSuccess(legacyUserId);
    } catch (err) {
      setError(err?.message || t('auth.err_network'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={closeAuth}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 120,
        background: 'rgba(10, 37, 64, 0.65)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        animation: 'cwmFadeIn 180ms ease-out',
        fontFamily: 'var(--cwm-font)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(420px, 100%)',
          background: '#fff',
          borderRadius: 24,
          padding: '28px 28px 24px',
          boxShadow: '0 30px 80px rgba(10,37,64,0.35), 0 4px 12px rgba(13,71,161,0.12)',
          position: 'relative',
          animation: 'cwmAlertIn 280ms cubic-bezier(.2,.9,.3,1.1)',
        }}
      >
        <button
          onClick={closeAuth}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            width: 32,
            height: 32,
            borderRadius: 999,
            border: 'none',
            background: 'rgba(10,37,64,0.06)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'var(--cwm-ink)',
          }}
        >
          <Icon name="close" size={18} />
        </button>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 18 }}>
          <Logo variant="primary" height={72} />
          <h2
            style={{
              fontSize: 22,
              fontWeight: 900,
              color: 'var(--cwm-ink)',
              margin: '12px 0 4px',
              letterSpacing: '-0.01em',
            }}
          >
            {isSignup ? t('auth.sign_up_title') : t('auth.sign_in_title')}
          </h2>
          <p style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--cwm-gray-500)', margin: 0, textAlign: 'center' }}>
            {isSignup ? t('auth.sign_up_sub') : t('auth.sign_in_sub')}
          </p>
        </div>

        <div
          style={{
            display: 'flex',
            background: 'var(--cwm-gray-100)',
            borderRadius: 999,
            padding: 4,
            marginBottom: 18,
          }}
        >
          {/* .map callback uses `opt` so we don't shadow the translator `t`. */}
          {[
            { key: 'signin', label: t('auth.tab_sign_in') },
            { key: 'signup', label: t('auth.tab_sign_up') },
          ].map((opt) => {
            const active = mode === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setMode(opt.key)}
                style={{
                  flex: 1,
                  padding: '8px 10px',
                  borderRadius: 999,
                  border: 'none',
                  cursor: 'pointer',
                  background: active ? '#fff' : 'transparent',
                  color: active ? 'var(--cwm-ink)' : 'var(--cwm-gray-500)',
                  fontWeight: 800,
                  fontSize: 13,
                  boxShadow: active ? '0 2px 8px rgba(13,71,161,0.12)' : 'none',
                  transition: 'background 160ms ease, color 160ms ease',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Google + Apple social buttons intentionally removed for now —
            the real OAuth plumbing (SDKs, client IDs, backend verify
            routes) isn't built, and a stub button that errors on click is
            worse than no button. Email sign-in below is the only path. */}

        <form onSubmit={submit} style={{ display: 'grid', gap: 10 }}>
          {isSignup && (
            <Field label={t('auth.full_name')} value={name} onChange={setName} placeholder={t('auth.name_placeholder')} autoComplete="name" />
          )}
          <Field
            label={t('auth.email')}
            type="email"
            value={email}
            onChange={setEmail}
            placeholder={t('auth.email_placeholder')}
            autoComplete="email"
          />
          <div>
            <Field
              label={t('auth.password')}
              type={showPw ? 'text' : 'password'}
              value={password}
              onChange={setPassword}
              placeholder={t('auth.password_placeholder')}
              autoComplete={isSignup ? 'new-password' : 'current-password'}
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              aria-label={showPw ? t('auth.hide_password') : t('auth.show_password')}
              style={{
                border: 'none',
                background: 'transparent',
                color: 'var(--cwm-blue-deep)',
                fontSize: 12,
                fontWeight: 800,
                cursor: 'pointer',
                padding: '4px 0 0',
                marginLeft: 'auto',
                display: 'block',
              }}
            >
              {showPw ? t('auth.hide_password') : t('auth.show_password')}
            </button>
          </div>

          {/* Terms + Privacy agreement — signup only. The submit button
              is visually de-emphasized until the box is ticked, and the
              handler in `submit()` hard-blocks the POST regardless of
              the visual state, so a user with no-JS-style forms can't
              bypass it. */}
          {isSignup && (
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              fontSize: 12.5,
              fontWeight: 500,
              lineHeight: 1.45,
              color: 'var(--cwm-gray-700)',
              cursor: 'pointer',
              userSelect: 'none',
              marginTop: 2,
            }}>
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                style={{
                  width: 16, height: 16,
                  marginTop: 2,
                  accentColor: 'var(--cwm-blue-deep, #0F7FE8)',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              />
              <span>
                {t('auth.agree_prefix')}{' '}
                <Link
                  to="/unified/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: 'var(--cwm-blue-deep)', fontWeight: 800, textDecoration: 'underline' }}
                >
                  {t('auth.agree_terms_link')}
                </Link>
                {' '}{t('auth.agree_and')}{' '}
                <Link
                  to="/unified/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: 'var(--cwm-blue-deep)', fontWeight: 800, textDecoration: 'underline' }}
                >
                  {t('auth.agree_privacy_link')}
                </Link>.
              </span>
            </label>
          )}

          {error && (
            <div
              role="alert"
              style={{
                background: '#FDECEC',
                color: '#B1202A',
                padding: '9px 12px',
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 600,
                border: '1px solid #F4C2C2',
              }}
            >
              {error}
            </div>
          )}
          <button
            type="submit"
            className="cwm-btn"
            disabled={submitting || (isSignup && !agreed)}
            style={{
              width: '100%',
              justifyContent: 'center',
              marginTop: 6,
              padding: '12px 16px',
              fontSize: 14.5,
              opacity: submitting || (isSignup && !agreed) ? 0.55 : 1,
              cursor: submitting
                ? 'wait'
                : (isSignup && !agreed) ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting
              ? (isSignup ? t('auth.submitting_sign_up') : t('auth.submitting_sign_in'))
              : (isSignup ? t('auth.submit_sign_up') : t('auth.submit_sign_in'))}
          </button>
        </form>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 12, fontWeight: 700 }}>
          {isSignup ? (
            <>
              <span style={{ color: 'var(--cwm-gray-500)' }}>{t('auth.have_account')}</span>
              <button
                type="button"
                onClick={() => setMode('signin')}
                style={{ background: 'none', border: 'none', color: 'var(--cwm-blue-deep)', cursor: 'pointer', fontWeight: 800 }}
              >
                {t('auth.tab_sign_in')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setMode('signup')}
                style={{ background: 'none', border: 'none', color: 'var(--cwm-blue-deep)', cursor: 'pointer', fontWeight: 800 }}
              >
                {t('auth.tab_sign_up')}
              </button>
              <button
                type="button"
                style={{ background: 'none', border: 'none', color: 'var(--cwm-gray-500)', cursor: 'pointer', fontWeight: 700 }}
              >
                {t('auth.forgot')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder, autoComplete }) {
  return (
    <label style={{ display: 'block' }}>
      <span
        style={{
          display: 'block',
          fontSize: 10.5,
          fontWeight: 800,
          color: 'var(--cwm-gray-500)',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          marginBottom: 4,
        }}
      >
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        style={{
          width: '100%',
          padding: '11px 14px',
          borderRadius: 12,
          border: '1.5px solid var(--cwm-gray-200)',
          fontSize: 14,
          fontFamily: 'inherit',
          color: 'var(--cwm-ink)',
          outline: 'none',
          transition: 'border-color 160ms ease, box-shadow 160ms ease',
          background: '#fff',
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = 'var(--cwm-blue)';
          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(30,155,255,0.18)';
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = 'var(--cwm-gray-200)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      />
    </label>
  );
}

